import styled, { css } from 'styled-components';
import dayjs from 'dayjs';
import React, { Fragment } from 'react';
import sortBy from 'lodash/sortBy';

import { formatHandicap, removePipes, withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import SGPLegsComponent from './sgp-legs-component';
import SuperParlayLegsComponent from './super-parlay-legs-component';
import { ResultType } from '@williamhill/rosi-ui-shared/src/types';
import { Block, Text } from '@williamhill/rosi-ui-shared/src/components/typography';
import { PriceComponent } from './price-component';
import { BetLeg, PriceOdds } from '../../model';

export type Leg = {
  id: string;
  event: { name: string };
  eventStartTime: string;
  market: { name: string };
  marketLine: number | null;
  price: PriceOdds | null;
  originalPrice?: PriceOdds | null;
  result: { type: ResultType } | null;
  selection: { name: string };
};

export default function LegsComponent({
  legs,
  betLegs,
  isParlay,
  isSGP
}: {
  legs: Leg[];
  betLegs: BetLeg[] | undefined;
  isParlay: boolean;
  isSGP: boolean;
}) {
  return (
    <>
      {isSGP ? (
        <SGPLegsComponent {...{ legs }} />
      ) : (
        <>
          {betLegs ? (
            <SuperParlayLegsComponent {...{ legs, betLegs }} />
          ) : (
            <>
              <hr />
              <LegsList as="ul" weight="medium">
                {sortBy(legs, ({ eventStartTime }) => new Date(eventStartTime).getTime()).map(
                  (
                    { id, event, eventStartTime, market, selection, price, originalPrice, result, marketLine },
                    index
                  ) => {
                    const withTestId = withRootTestId(`bet-tracker-bets-ticket-leg-${id}`);
                    const selectionName = removePipes(selection.name);
                    return (
                      <Fragment key={id}>
                        <LegItem
                          data-testid={withTestId()}
                          icon={result && iconsByResultType[result.type]}
                          parlayMode={isParlay}>
                          <LegSection>
                            <header>
                              <SelectionNameContainer data-testid={withTestId('name')}>
                                {selectionName}
                                {marketLine && ` ${formatHandicap(marketLine, selectionName, false)}`}
                              </SelectionNameContainer>
                              <PriceComponent
                                price={price}
                                originalPrice={originalPrice}
                                isVoided={Boolean(result && result.type === 'void')}
                              />
                            </header>
                            <main>
                              <MarketNameContainer data-testid={withTestId('market')}>
                                {removePipes(market.name)}
                              </MarketNameContainer>
                              <Text as="div" size="sm" weight="regular">
                                {dayjs(eventStartTime).format('MMM DD, h:mma')} |{' '}
                                <EventNameSpan data-testid={withTestId('event-name')}>
                                  {removePipes(event.name)}
                                </EventNameSpan>
                              </Text>
                            </main>
                          </LegSection>
                        </LegItem>
                        {legs.length - 1 !== index && <LineBreak />}
                      </Fragment>
                    );
                  }
                )}
              </LegsList>
            </>
          )}
        </>
      )}
    </>
  );
}

const LegsList = styled(Text)`
  position: relative;
  display: flex;
  flex-direction: column;
  list-style-type: none;
  margin: 0;
  padding: 0.8rem 0;
`;

const LineBreak = styled.hr`
  border-style: none;
  border-top: 1px solid ${({ theme }) => theme.colors.grey200};
  height: 1px;
  margin: 0.8rem 0;
`;

export const iconsByResultType = {
  lost: { color: 'error', content: 'L' },
  push: { color: 'warning', content: 'P' },
  void: { color: 'expired', content: 'V' },
  won: { color: 'success', content: 'W' },
  place: { color: 'error', content: 'P' }
};

const LegItem = styled.li<{ icon?: { color: string; content: string }; parlayMode: boolean }>`
  & > section {
    margin-left: 2.2rem;
  }

  ${({ icon, theme }) => {
    if (icon) {
      return css`
        &:before {
          display: flex;
          justify-content: center;
          background-color: ${theme.colors.modals[icon.color]};
          color: ${theme.colors.white};
          /* prettier-ignore */
          content: "${icon.content}";
          position: absolute;
          padding: 0.1rem;
          border-radius: 50%;
          font-size: 1rem;
          margin-top: 0.3rem;
          height: 1.3rem;
          width: 1.3rem;
        }
      `;
    }

    return css`
      &:before {
        display: flex;
        justify-content: center;
        background-color: ${theme.colors.modals.neutral};
        /* prettier-ignore */
        content: "";
        position: absolute;
        padding: 0.1rem;
        border-radius: 50%;
        font-size: 1rem;
        margin-top: 0.3rem;
        height: 1.3rem;
        width: 1.3rem;
      }
    `;
  }};
`;

export const LegSection = styled.section`
  & > header {
    display: flex;
  }
`;

export const SelectionNameContainer = styled(Block).attrs({ weight: 'bold' })`
  flex-grow: 1;
`;

export const MarketNameContainer = styled(Block).attrs({ weight: 'regular' })`
  color: ${({ theme }) => theme.colors.brand.quaternary};
`;

export const EventNameSpan = styled.span`
  color: ${({ theme }) => theme.colors.brand.quaternary};
`;
